import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/cn";
import {
  activatePromptVersion,
  listPromptVersions,
  previewPrompt,
  updatePrompt,
  type PromptTemplate,
} from "@/api/prompts";
import { renderMarkdown } from "@/lib/markdown";
import {
  categoryColor,
  categoryLabel,
  formatPromptDate,
  parsePatternLabel,
  promptContextRequirements,
  promptRequirementSummary,
} from "@/lib/prompt-ui";

type PromptViewMode = "formatted" | "raw" | "edit";

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:text-foreground"
    >
      {copied ? "Copied!" : label}
    </button>
  );
}

function PlaceholderText({ text }: { text: string }) {
  const parts = text.split(/(\{\{[^}]+\}\})/g);

  return (
    <>
      {parts.map((part, index) =>
        /^\{\{[^}]+\}\}$/.test(part) ? (
          <span
            key={index}
            className="rounded bg-brand/10 px-1 py-0.5 font-mono text-[11px] text-brand"
          >
            {part}
          </span>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
}

function MetaSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-foreground">{value}</dd>
    </div>
  );
}

export function PromptDetailContent({ prompt }: { prompt: PromptTemplate }) {
  const qc = useQueryClient();
  const contextFlags = promptContextRequirements(prompt);
  const createdLabel = formatPromptDate(prompt.createdAt);
  const updatedLabel = formatPromptDate(prompt.updatedAt);

  const [viewMode, setViewMode] = useState<PromptViewMode>("formatted");
  const [draftPrompt, setDraftPrompt] = useState(prompt.systemPrompt ?? "");
  const [draftChangelog, setDraftChangelog] = useState("");
  const [previewInput, setPreviewInput] = useState(
    "Sample opportunity: Acme Corp — enterprise renewal discussion.",
  );
  const [previewResult, setPreviewResult] = useState<{
    prompt: string;
    unreplaced: string[];
  } | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const versionsQuery = useQuery({
    queryKey: ["prompt-versions", prompt.kind],
    queryFn: () => listPromptVersions(prompt.kind),
    staleTime: 15_000,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      updatePrompt(prompt.kind, {
        name: prompt.name,
        description: prompt.description,
        systemPrompt: draftPrompt,
        outputContract: prompt.outputContract,
        parsePattern: prompt.parsePattern,
        allowedTools: prompt.allowedTools,
        disallowedTools: prompt.disallowedTools,
        maxDuration: prompt.maxDuration,
        model: prompt.model,
        requiresCv: prompt.requiresCv,
        requiresProfile: prompt.requiresProfile,
        requiresMemory: prompt.requiresMemory,
        requiresDedup: prompt.requiresDedup,
        requiresContext: prompt.requiresContext,
        category: prompt.category,
        changelog: draftChangelog || `Edited via Prompt Ops (${new Date().toISOString()})`,
      }),
    onSuccess: async () => {
      setStatusMsg("Saved new version.");
      setDraftChangelog("");
      setViewMode("formatted");
      await qc.invalidateQueries({ queryKey: ["prompt", prompt.kind] });
      await qc.invalidateQueries({ queryKey: ["prompt-versions", prompt.kind] });
      await qc.invalidateQueries({ queryKey: ["prompts"] });
    },
    onError: (err) => {
      setStatusMsg(err instanceof Error ? err.message : "Save failed");
    },
  });

  const activateMutation = useMutation({
    mutationFn: (version: number) => activatePromptVersion(prompt.kind, version),
    onSuccess: async () => {
      setStatusMsg("Version activated.");
      await qc.invalidateQueries({ queryKey: ["prompt", prompt.kind] });
      await qc.invalidateQueries({ queryKey: ["prompt-versions", prompt.kind] });
      await qc.invalidateQueries({ queryKey: ["prompts"] });
    },
    onError: (err) => {
      setStatusMsg(err instanceof Error ? err.message : "Activate failed");
    },
  });

  const previewMutation = useMutation({
    mutationFn: () => previewPrompt(prompt.kind, previewInput),
    onSuccess: (res) => {
      setPreviewResult({
        prompt: res.assembled.prompt,
        unreplaced: res.unreplacedPlaceholders,
      });
    },
    onError: (err) => {
      setStatusMsg(err instanceof Error ? err.message : "Preview failed");
    },
  });

  const systemPrompt = prompt.systemPrompt?.trim() || "";

  return (
    <div className="lg:grid lg:grid-cols-3 lg:gap-6">
      <div className="mb-6 space-y-4 lg:col-span-1 lg:mb-0">
        <MetaSection title="Template">
          <dl className="space-y-3">
            <MetaRow label="Kind" value={<code className="text-xs">{prompt.kind}</code>} />
            <MetaRow label="Version" value={`v${prompt.version}`} />
            <MetaRow
              label="Status"
              value={
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px]",
                    prompt.isActive
                      ? "bg-success/10 text-success"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {prompt.isActive ? "Active" : "Inactive"}
                </span>
              }
            />
            {prompt.category && (
              <MetaRow
                label="Category"
                value={
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-medium",
                      categoryColor(prompt.category),
                    )}
                  >
                    {categoryLabel(prompt.category)}
                  </span>
                }
              />
            )}
          </dl>
        </MetaSection>

        <MetaSection title="Context required">
          <p className="mb-2 text-xs text-muted-foreground">
            {promptRequirementSummary(prompt)}
          </p>
          {contextFlags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {contextFlags.map((flag) => (
                <span
                  key={flag}
                  className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
                >
                  {flag}
                </span>
              ))}
            </div>
          )}
          {prompt.requiresContext && prompt.requiresContext.length > 0 && (
            <div className="mt-3">
              <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Context fragments
              </p>
              <ul className="space-y-1">
                {prompt.requiresContext.map((ctx) => (
                  <li key={ctx}>
                    <code className="text-[11px] text-muted-foreground">{ctx}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </MetaSection>

        <MetaSection title="Execution">
          <dl className="grid gap-3">
            <MetaRow
              label="Parse pattern"
              value={parsePatternLabel(prompt.parsePattern)}
            />
            <MetaRow
              label="Max tokens"
              value={String(prompt.maxDuration ?? 8192)}
            />
            {prompt.model && <MetaRow label="Model" value={prompt.model} />}
          </dl>
        </MetaSection>

        <MetaSection title="Versions">
          {versionsQuery.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : (versionsQuery.data ?? []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No archives yet.</p>
          ) : (
            <ul className="space-y-2">
              {(versionsQuery.data ?? []).map((v) => (
                <li
                  key={`${v.name}-v${v.version}`}
                  className="flex items-center justify-between gap-2 text-xs"
                >
                  <span className="text-muted-foreground">
                    v{v.version}
                    {v.changelog ? ` · ${v.changelog.slice(0, 40)}` : ""}
                  </span>
                  <button
                    type="button"
                    className="rounded border border-border px-2 py-0.5 hover:bg-muted/40"
                    disabled={
                      activateMutation.isPending || v.version === prompt.version
                    }
                    onClick={() => activateMutation.mutate(v.version)}
                  >
                    Activate
                  </button>
                </li>
              ))}
            </ul>
          )}
        </MetaSection>

        {(createdLabel || updatedLabel) && (
          <MetaSection title="Metadata">
            <dl className="grid gap-3">
              {createdLabel && <MetaRow label="Created" value={createdLabel} />}
              {updatedLabel && <MetaRow label="Updated" value={updatedLabel} />}
            </dl>
          </MetaSection>
        )}
      </div>

      <div className="space-y-4 lg:col-span-2">
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="text-lg font-bold text-foreground">{prompt.name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {prompt.description || "No description available."}
          </p>
        </div>

        {statusMsg && (
          <p className="rounded-md border border-border bg-muted/20 px-3 py-2 text-xs text-foreground">
            {statusMsg}
          </p>
        )}

        <div className="rounded-lg border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                System prompt
              </h3>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Edit saves a new version and archives the previous active.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex gap-1 rounded-lg border border-border p-1">
                {(["formatted", "raw", "edit"] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      if (mode === "edit") setDraftPrompt(prompt.systemPrompt ?? "");
                      setViewMode(mode);
                    }}
                    className={cn(
                      "rounded-md px-2.5 py-1 text-xs font-medium transition",
                      viewMode === mode
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {mode === "formatted"
                      ? "Formatted"
                      : mode === "raw"
                        ? "Raw"
                        : "Edit"}
                  </button>
                ))}
              </div>
              {systemPrompt && viewMode !== "edit" && (
                <CopyButton text={systemPrompt} label="Copy prompt" />
              )}
            </div>
          </div>

          <div className="max-h-[min(70vh,640px)] overflow-y-auto px-5 py-4">
            {viewMode === "edit" ? (
              <div className="space-y-3">
                <textarea
                  className="min-h-[280px] w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs leading-relaxed"
                  value={draftPrompt}
                  onChange={(e) => setDraftPrompt(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Changelog (optional)"
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  value={draftChangelog}
                  onChange={(e) => setDraftChangelog(e.target.value)}
                />
                <button
                  type="button"
                  className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
                  disabled={saveMutation.isPending || !draftPrompt.trim()}
                  onClick={() => saveMutation.mutate()}
                >
                  {saveMutation.isPending ? "Saving…" : "Save new version"}
                </button>
              </div>
            ) : !systemPrompt ? (
              <p className="text-sm text-muted-foreground">(No system prompt text)</p>
            ) : viewMode === "raw" ? (
              <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-foreground">
                <PlaceholderText text={systemPrompt} />
              </pre>
            ) : (
              <div
                className="prose-sm max-w-none text-foreground [&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(systemPrompt) }}
              />
            )}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Assemble preview (no model call)
          </h3>
          <textarea
            className="mb-2 min-h-[72px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={previewInput}
            onChange={(e) => setPreviewInput(e.target.value)}
          />
          <button
            type="button"
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50"
            disabled={previewMutation.isPending}
            onClick={() => previewMutation.mutate()}
          >
            {previewMutation.isPending ? "Assembling…" : "Preview assembled prompt"}
          </button>
          {previewResult && (
            <div className="mt-3 space-y-2">
              {previewResult.unreplaced.length > 0 && (
                <p className="text-xs text-amber-600">
                  Unreplaced: {previewResult.unreplaced.join(", ")}
                </p>
              )}
              <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-bg px-3 py-2 font-mono text-[11px]">
                {previewResult.prompt}
              </pre>
            </div>
          )}
        </div>

        {prompt.outputContract && (
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Output contract
              </h3>
              <CopyButton text={prompt.outputContract} label="Copy" />
            </div>
            <pre className="whitespace-pre-wrap rounded-md border border-border bg-bg px-4 py-3 font-mono text-xs leading-relaxed text-foreground">
              {prompt.outputContract}
            </pre>
          </div>
        )}

        {prompt.changelog && (
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Changelog
            </h3>
            <p className="text-sm text-muted-foreground">{prompt.changelog}</p>
          </div>
        )}
      </div>
    </div>
  );
}
